import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  ExpandMore,
  CalendarToday,
  AccessTime,
  Stop as StopIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import ImpersonationBanner from './ImpersonationBanner';
import { impersonationAPI } from '../services/api';

const Layout: React.FC = () => {
  const { user, logout, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Nascondi sidebar e header quando si è su mobile-presence (versione mobile standalone)
  const isMobilePresencePage = location.pathname === '/mobile-presence';
  const hideLayout = isMobilePresencePage;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [endingImpersonation, setEndingImpersonation] = React.useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleEndImpersonation = async () => {
    setEndingImpersonation(true);
    try {
      // Se siamo in modalità "vedi admin", ripristina prima l'ID per poter terminare la sessione
      const viewAdminMode = localStorage.getItem('view_admin_mode') === 'true';
      const backupId = localStorage.getItem('impersonated_user_id_backup');
      
      // Se siamo in modalità "vedi admin", ripristina l'ID per poter terminare la sessione
      if (viewAdminMode && backupId) {
        localStorage.setItem('impersonated_user_id', backupId);
      }
      
      // Chiama l'API per terminare la sessione sul backend
      try {
        await impersonationAPI.endImpersonation();
      } catch (apiError) {
        // Continua comunque a pulire localStorage anche se l'API fallisce
      }
      
      // Rimuovi TUTTO da localStorage
      localStorage.removeItem('impersonated_user_id');
      localStorage.removeItem('impersonated_user_id_backup');
      localStorage.removeItem('impersonation_session_backup');
      localStorage.removeItem('view_admin_mode');
      
      // Verifica che tutto sia stato rimosso
      const verifyRemoval = localStorage.getItem('impersonated_user_id') || 
                            localStorage.getItem('impersonated_user_id_backup') || 
                            localStorage.getItem('view_admin_mode');
      if (verifyRemoval) {
        // Forza la rimozione
        localStorage.removeItem('impersonated_user_id');
        localStorage.removeItem('impersonated_user_id_backup');
        localStorage.removeItem('impersonation_session_backup');
        localStorage.removeItem('view_admin_mode');
      }
      
      // Ricarica la pagina reindirizzando direttamente alla dashboard admin
      window.location.href = '/dashboard'; // Reindirizza direttamente alla dashboard admin
    } catch (error) {
      alert('Errore durante la terminazione dell\'impersonazione');
      // Anche in caso di errore, prova a pulire localStorage e ricaricare
      localStorage.removeItem('impersonated_user_id');
      localStorage.removeItem('impersonated_user_id_backup');
      localStorage.removeItem('impersonation_session_backup');
      localStorage.removeItem('view_admin_mode');
      window.location.href = '/dashboard';
    } finally {
      setEndingImpersonation(false);
      handleMenuClose();
    }
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Se siamo sulla pagina mobile-presence, renderizza solo il contenuto senza layout
  if (hideLayout) {
    return <Outlet />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: '#0d47a1',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
          borderRadius: 0
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box display="flex" alignItems="center" gap={1}>
            <AccessTime sx={{ fontSize: 28, color: '#ffffff' }} />
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#ffffff',
                fontWeight: 'bold',
                fontSize: '1.5rem'
              }}
            >
              Monitoraggio Presenze
            </Typography>
          </Box>

          {/* User Info and Menu */}
          <Box display="flex" alignItems="center" gap={2}>
            {/* User Name */}
            <Typography variant="body1" sx={{ color: '#ffffff' }}>
              {user ? `${user.cognome} ${user.nome}`.toUpperCase() : 'UTENTE'}
            </Typography>
            
            {/* Date */}
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday sx={{ fontSize: 16, color: '#ffffff' }} />
              <Typography variant="body2" sx={{ color: '#ffffff' }}>
                {formatDate()}
              </Typography>
            </Box>

            {/* User Menu */}
            <IconButton
              size="large"
              onClick={handleMenuOpen}
              sx={{ color: '#ffffff' }}
            >
              <AccountCircle />
              <ExpandMore sx={{ ml: 0.5 }} />
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {(isImpersonating || localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup')) && (
                <MenuItem onClick={handleEndImpersonation} disabled={endingImpersonation}>
                  <StopIcon sx={{ mr: 1 }} />
                  Termina accesso impersonato
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Impersonation Banner */}
      <ImpersonationBanner />

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Sidebar */}
        <Sidebar />

        {/* Center Content */}
        <Box 
          sx={{ 
            flexGrow: 1, 
            overflow: 'auto',
            backgroundColor: '#fafafa',
            px: 2,
            py: 0
          }}
        >
          <Outlet />
        </Box>

      </Box>
    </Box>
  );
};

export default Layout;